import {useCallback, useSyncExternalStore} from 'react';

import type {ConfigManager} from './ConfigManager';

const isCallable = <T>(t: unknown): t is (value: T) => T =>
    typeof t === 'function';

export const configHook =
    <Config extends Record<string, unknown>>(config: ConfigManager<Config>) =>
    <T extends keyof Config>(key: T) => {
        const value = useSyncExternalStore(
            onChange => config.subscribe(key, onChange),
            () => config.get(key),
        );

        const handleSetValue = useCallback(
            (
                input: ((prevValue: Config[T]) => Config[T]) | Config[T],
                persist?: boolean,
            ) => {
                // updating the value will trigger the subscriber above
                config.set({
                    key,
                    value: isCallable<Config[T]>(input)
                        ? input(config.get(key))
                        : input,
                    persist,
                });
            },
            [key, config],
        );

        return [value, handleSetValue] as const;
    };
